import type ReactMarkdown from 'react-markdown';

import { Suspense, lazy, memo, type ComponentProps } from 'react';

import { Text } from '../Text/Text';

// The markdown rendering stack (react-markdown + rehype-raw + rehype-sanitize +
// remark-gfm, which transitively pulls in parse5) is large and not needed for
// the initial render, so it is lazy-loaded and code-split out of the entry
// chunk. The dependency only downloads when a <Markdown> first renders.
//
// `import type` below is erased at build time, so it does not pull react-markdown
// back into the entry; it only provides the `components` prop type.
const MarkdownRenderer = lazy(() => import('./MarkdownRenderer'));

interface Props extends ComponentProps<typeof Text> {
  /** Simple markdown string (e.g. 'hello', '# hello') */
  text: string;
  /** This prop can be used to render custom components for markdown elements. */
  components?: ComponentProps<typeof ReactMarkdown>['components'];
  options?: {
    allowLinks?: {
      disabledTooltip: string;
      regexp: RegExp;
    };
  };
}

export const Markdown = memo(
  ({ text, components, options, ...rest }: Props) => {
    return (
      <Text {...rest}>
        {/* Render nothing while the renderer chunk loads so unrendered markdown
            is never shown; the text appears once the chunk resolves (typically
            cached after first use). */}
        <Suspense fallback={null}>
          <MarkdownRenderer
            text={text}
            components={components}
            options={options}
          />
        </Suspense>
      </Text>
    );
  },
);
